import { FC, useEffect, useRef, useState } from "react";
import { Box, Button, Flex, HStack, SimpleGrid, Text } from "@chakra-ui/react";

import { EvalResultModel } from "@shared/models";
import { FONT_WEIGHT } from "@assets/theme/foundations/textStyle";

import { ScoreBadge } from "../ScoreBadge";

interface EvalResultCardProps {
  result: EvalResultModel;
}

interface EvalResultSectionProps {
  title: string;
  content: string;
  clampTo?: number;
  muted?: boolean;
}

const EvalResultSection: FC<EvalResultSectionProps> = ({ title, content, clampTo, muted }) => {
  const contentRef = useRef<HTMLParagraphElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);

  const toggleExpanded = () => setIsExpanded((expanded) => !expanded);

  useEffect(() => {
    const element = contentRef.current;

    if (!element || !clampTo || isExpanded) return;

    setIsClamped(element.scrollHeight > element.clientHeight + 1);
  }, [clampTo, content, isExpanded]);

  return (
    <Box mt="14px">
      <Text
        fontSize="11px"
        fontWeight={FONT_WEIGHT.HEAVY}
        letterSpacing="0.6px"
        textTransform="uppercase"
        color="brand.tableHeaderText"
        mb="4px"
      >
        {title}
      </Text>
      <Text
        ref={contentRef}
        fontSize="14px"
        lineHeight="21px"
        color={muted ? "brand.grey" : "brand.tableBodyText"}
        maxW="90ch"
        lineClamp={isExpanded ? undefined : clampTo}
        whiteSpace="pre-wrap"
      >
        {content}
      </Text>
      {isClamped && (
        <Button
          variant="link"
          mt="4px"
          fontSize="12px"
          color="brand.primary"
          fontWeight={FONT_WEIGHT.HEAVY}
          onClick={toggleExpanded}
        >
          {isExpanded ? "Show less" : "Show more"}
        </Button>
      )}
    </Box>
  );
};

const EvalResultCard: FC<EvalResultCardProps> = ({ result }) => (
  <Box
    bg="white"
    borderWidth="1px"
    borderColor="gray.50"
    borderRadius="8px"
    p="16px"
    transition="border-color 0.2s"
    _hover={{ borderColor: "grey.10" }}
  >
    <Flex justify="space-between" align="center" gap="12px">
      <HStack gap="8px">
        <Text fontFamily="mono" fontSize="13px" fontWeight={FONT_WEIGHT.HEAVY}>
          {result.case_id}
        </Text>
        <Text
          fontSize="10px"
          letterSpacing="0.6px"
          textTransform="uppercase"
          color="blue.700"
          bg="grey.5"
          px="8px"
          py="2px"
          borderRadius="4px"
        >
          {result.category}
        </Text>
      </HStack>
      <Text fontSize="12px" color="brand.grey" whiteSpace="nowrap">
        {result.latency_ms}ms
      </Text>
    </Flex>

    <SimpleGrid columns={{ base: 1, "2xl": 2 }} gapX="32px" gapY="0">
      <EvalResultSection title="Question" content={result.input_query} />
      {!!result.expected_answer && <EvalResultSection title="Expected answer" content={result.expected_answer} muted />}
    </SimpleGrid>
    {!!result.context && <EvalResultSection title="Context" content={result.context} clampTo={3} muted />}
    <EvalResultSection title="Response" content={result.response} clampTo={5} />

    <Flex gap="8px" mt="16px" flexWrap="wrap">
      {result.scores.map((score) => (
        <ScoreBadge key={score.criterion} criterion={score.criterion} score={score.score} size="sm" />
      ))}
    </Flex>
  </Box>
);

export default EvalResultCard;
